import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Ticket, Train, Calendar, CreditCard, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';

import { API } from '../config';

const Checkout = () => {
  const { items, restaurant, total, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [pnr, setPnr] = useState('');
  const [trainNo, setTrainNo] = useState('');
  const [coach, setCoach] = useState('');
  const [berth, setBerth] = useState('');
  const [date, setDate] = useState('');
  const [payment, setPayment] = useState('cod');
  const [loading, setLoading] = useState(false);

  const deliveryFee = 29;
  const tax = Math.round(total * 0.05);
  const grand = total + deliveryFee + tax;

  const place = async () => {
    if (!user) { toast({ title: 'Please login first' }); nav('/login'); return; }
    if (pnr.length !== 10) { toast({ title: 'Enter valid PNR' }); return; }
    if (!trainNo || !coach || !berth || !date) { toast({ title: 'Fill all delivery details' }); return; }
    setLoading(true);
    try {
      const t = localStorage.getItem('tp_token');
      const r = await axios.post(`${API}/orders`, {
        restaurant_id: restaurant.id,
        restaurant_name: restaurant.name,
        station_code: restaurant.station,
        pnr, train_number: trainNo, coach, berth, delivery_date: date,
        items: items.map(i => ({ id: i.id, name: i.name, price: i.price, qty: i.qty })),
        subtotal: total, delivery_fee: deliveryFee, tax, total: grand, payment_method: payment,
      }, { headers: { Authorization: `Bearer ${t}` } });
      clear();
      nav(`/order-success/${r.data.id}`);
    } catch (e) { toast({ title: 'Order failed', description: e.response?.data?.detail || 'Try again' }); }
    finally { setLoading(false); }
  };

  if (items.length === 0) { nav('/food-on-train'); return null; }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold text-[#0B2E5C] mb-4">Checkout</h1>
      <div className="grid md:grid-cols-[1fr_360px] gap-5">
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-100 card-shadow p-5">
            <h3 className="font-bold text-[#0B2E5C] mb-4 flex items-center gap-2"><Train size={18} className="text-tp-orange" /> Delivery to Seat</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field icon={Ticket} label="PNR (10-digit)" value={pnr} onChange={v=>setPnr(v.replace(/\D/g,'').slice(0,10))} placeholder="8921456732" />
              <Field icon={Train} label="Train Number" value={trainNo} onChange={setTrainNo} placeholder="12951" />
              <Field label="Coach" value={coach} onChange={v=>setCoach(v.toUpperCase())} placeholder="B4" />
              <Field label="Berth/Seat" value={berth} onChange={setBerth} placeholder="32" />
              <Field icon={Calendar} label="Delivery Date" value={date} onChange={setDate} placeholder="DD MMM YYYY" />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 card-shadow p-5">
            <h3 className="font-bold text-[#0B2E5C] mb-3 flex items-center gap-2"><CreditCard size={18} className="text-tp-orange" /> Payment Method</h3>
            {['cod','upi','card'].map(p => (
              <label key={p} className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer mb-2 ${payment===p ? 'border-tp-orange bg-[#FFF3E6]' : 'border-gray-200'}`}>
                <input type="radio" checked={payment===p} onChange={()=>setPayment(p)} className="accent-[#F68B1F]" />
                <span className="font-semibold text-sm text-[#0B2E5C]">{p==='cod' ? 'Cash on Delivery' : p==='upi' ? 'UPI / PhonePe / GPay' : 'Credit / Debit Card'}</span>
                {p==='cod' && <span className="ml-auto text-[11px] text-green-700 bg-green-100 px-2 py-0.5 rounded">Recommended</span>}
              </label>
            ))}
            <p className="text-[11px] text-gray-400 mt-2">MOCKED payment — no real charge will be made.</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 card-shadow p-5 h-fit">
          <h3 className="font-bold text-[#0B2E5C] mb-3">Bill Summary</h3>
          <div className="text-sm space-y-1 text-gray-600">
            {items.map(i => (<div key={i.id} className="flex justify-between"><span>{i.name} × {i.qty}</span><span>₹{i.price * i.qty}</span></div>))}
            <div className="flex justify-between pt-2 border-t"><span>Subtotal</span><span>₹{total}</span></div>
            <div className="flex justify-between"><span>Delivery fee</span><span>₹{deliveryFee}</span></div>
            <div className="flex justify-between"><span>Taxes</span><span>₹{tax}</span></div>
            <div className="flex justify-between font-bold text-[#0B2E5C] text-base pt-2 border-t border-dashed"><span>Total</span><span>₹{grand}</span></div>
          </div>
          <Button onClick={place} className="w-full mt-4 bg-tp-orange hover:bg-[#E07A0F] text-white font-bold h-11" disabled={loading}>
            {loading ? <Loader2 className="animate-spin" size={18} /> : `Place Order ₹${grand}`}
          </Button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ icon: Icon, label, value, onChange, placeholder }) => (
  <div>
    <label className="text-xs font-semibold text-gray-500 uppercase">{label}</label>
    <div className="mt-1 flex items-center gap-2 border-2 border-gray-200 rounded-lg focus-within:border-[#0B2E5C] px-3">
      {Icon && <Icon size={14} className="text-gray-400" />}
      <Input value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} className="border-0 focus-visible:ring-0 h-11" />
    </div>
  </div>
);

export default Checkout;
